<template>
    <div class="game-round-container" v-if="activeRoundId">
        <current-assets ref="currentAssetsMenu"
                        :assets="localAssets"
                        :land-sale-elections="currentLandSaleElections"
                        :land-purchase-elections="currentLandPurchaseElections"
                        :building-sale-elections="currentBuildingSaleElections"
                        :building-purchase-elections="currentBuildingPurchaseElections"
                        :equipment-sale-elections="currentEquipmentSaleElections"
                        :equipment-purchase-elections="currentEquipmentPurchaseElections"
                        :store-furnishing-sale-elections="currentStoreFurnishingSaleElections"
                        :store-furnishing-purchase-elections="currentStoreFurnishingPurchaseElections"
                        :debts-borrowed-elections="currentDebtsBorrowedElections"
                        :debts-repayed-elections="currentDebtsRepayedElections"
                        :product-elections="currentProductElections"
                        :show-inventory="routeDetail === 'go-to-market'"
                        :loan-cap="loanCap"
                        :bond-cap="bondCap"
                        :is-valid.sync="isSubmissionAllowed"
                        :should-require-positive-cash-balance-before-electing="shouldRequirePositiveCashBalanceBeforeElecting"
                        :production-cost="productionCost"
                        :stock-sale-elections="currentStockSaleElections"
                        :bond-sale-elections="currentBondSaleElections"
                        :additional-stock-sale-benefit="additionalStockSaleBenefit"
                        :total-borrow-cap="totalBorrowCap"
                        :prior-loan-amount="priorLoanAmount"
                        :prior-bonds-sold-principal="priorBondsSoldPrincipal"
                        :is-equipment-enabled-for-game="isEquipmentEnabledForGame"
                        :is-store-furnishing-enabled-for-game="isStoreFurnishingEnabledForGame"
        ></current-assets>
        <div class="gameBoard">
            <div class="elections-alert">
                <div v-if="showFormError && !dismissAlert" class="alert-error">
                    {{errorMessage}}
                    <span @click="clearAlert()"><md-icon class="fa fa-times-circle light"></md-icon></span>
                </div>
                <div v-if="teamHasSubmittedElections && !hasSuccessfullySubmitted" class="alert-success">
                    <span>
                        Team has submitted elections
                        <br>
                        Submitted by {{getCurrentRoundElections.submittedBy.firstname}} {{getCurrentRoundElections.submittedBy.lastname}} at {{getFormattedDate(parseInt(getCurrentRoundElections.createdAt)/1000, getTeam.semester.timezone)}}
                    </span>
                </div>
                <div v-if="!teamHasSubmittedElections && !hasSuccessfullySubmitted" class="alert-swpurp">
                    <span>
                        Warning! Elections have not yet been submitted for this round
                    </span>
                </div>
                <div v-if="hasSuccessfullySubmitted" class="alert-success alert-success-pulse">
                    <span v-if="hasSuccessfullySubmitted">You successfully submitted for your team!</span>
                </div>
            </div>
            <div class="elections-alert-mobile">
                <div v-if="showFormError">
                    <md-icon class="fas fa-exclamation-circle error"><md-tooltip class="tooltip" md-direction="top">{{errorMessage}}</md-tooltip></md-icon>
                </div>
                <div v-if="teamHasSubmittedElections">
                    <md-icon class="fa fa-check-circle success"><md-tooltip class="tooltip" md-direction="top">Team has submitted elections. Submitted by {{getCurrentRoundElections.submittedBy.firstname}} {{getCurrentRoundElections.submittedBy.lastname}} at {{getFormattedDate(parseInt(getCurrentRoundElections.createdAt)/1000, getTeam.semester.timezone)}}</md-tooltip></md-icon>
                </div>
                <div v-else>
                    <md-icon class="fas fa-exclamation-triangle salmon"><md-tooltip class="tooltip" md-direction="top">Warning! Elections have not yet been submitted for this round</md-tooltip></md-icon>
                </div>
            </div>
            <div v-if="routeDetail === 'get-cash'">
                <div class="countdown" v-if="shouldShowCountdown">
                    <Countdown :deadline="currentDeadline" :countdown-id="'get-cash'" :hide-labels="true" :hide-big-units="true"></Countdown>
                    <div class="remaining-label">remaining</div>
                </div>
                <div class="tab-header">
                    <div class="center-text welcome" v-if="getCurrentGameState.roundIndex === 0">Welcome to your new game with Accountonomics!</div>
                    <h1>Get Cash</h1>
                    <div class="preloaded-stock" v-if="getCurrentGameState.roundIndex === 0 && initialOfferingNumShares > 0">
                      <div>Your company has sold <b>{{initialOfferingNumShares}} shares of common stock</b> at the opening price of <b>{{teamStartingStockPrice | toCurrency}} per share</b></div>
                      <div>In future rounds, your company's stock price will fluctuate to reflect the value of your company</div>
                    </div>
                    <div>
                      Sell assets and take out loans below
                    </div>
                </div>
                <div class="inputSheetStep">
                    <section class="section-container" v-if="noDebt && (!localAssets || (localAssets.lands.length === 0 && localAssets.buildings.length === 0 && localAssets.equipments.length === 0))">
                      <div class="center-text" v-if="getCurrentGameState.roundIndex === 0">Your team doesn't own any assets to sell yet</div>
                      <div class="center-text" v-else>Your team doesn't currently own any assets to sell</div>
                    </section>
                    <section class="section-container" v-if="!noDebt">
                        <h3 class="section-header">↓ Borrow Loans ↓</h3>
                        <debt :debts-borrowed-elections.sync="currentDebtsBorrowedElections" :loan-cap="loanCap" :total-borrow-cap="totalBorrowCap" :prior-loan-amount="priorLoanAmount"></debt>
                    </section>
                    <section class="section-container" v-if="!noStock">
                        <h3 class="section-header">↓ Sell Common Stock ↓</h3>
                        <stock :stock-sale-elections.sync="currentStockSaleElections" :active-round-id="activeRoundId"></stock>
                    </section>
                    <section class="section-container" v-if="!noBonds">
                        <h3 class="section-header">↓ Sell Bonds ↓</h3>
                        <bond :bond-sale-elections.sync="currentBondSaleElections" :active-round-id="activeRoundId" :bond-cap="bondCap" :total-borrow-cap="totalBorrowCap" :prior-bonds-sold-principal="priorBondsSoldPrincipal"></bond>
                    </section>
                    <section class="section-container" v-if="localAssets && localAssets.lands.length">
                        <h3 class="section-header">↓ Sell Land ↓</h3>
                        <ms-asset v-model="localAssets.lands"
                                  :sale-elections.sync="currentLandSaleElections"
                                  :selected-assets.sync="soldLandIds"
                                  assetName="Land"
                                  :active-round-id="activeRoundId">
                        </ms-asset>
                    </section>
                    <section class="section-container" v-if="localAssets && localAssets.buildings.length">
                        <h3 class="section-header">
                            ↓ Sell Building ↓
                            <md-icon v-if="buildingsExpiringNow && buildingsExpiringNow.length" class="fa fa-exclamation-circle"></md-icon>
                        </h3>
                        <ms-asset v-model="localAssets.buildings"
                                  :sale-elections.sync="currentBuildingSaleElections"
                                  :selected-assets.sync="soldBuildingIds"
                                  assetName="Building"
                                  :active-round-id="activeRoundId">
                        </ms-asset>
                    </section>
                    <section class="section-container" v-if="localAssets && localAssets.equipments.length">
                        <h3 class="section-header">
                            ↓ Sell Equipment ↓
                            <md-icon v-if="equipmentsExpiringNow && equipmentsExpiringNow.length" class="fa fa-exclamation-circle"></md-icon>
                        </h3>
                        <ms-asset v-model="localAssets.equipments"
                                  :sale-elections.sync="currentEquipmentSaleElections"
                                  :selected-assets.sync="soldEquipmentIds"
                                  assetName="Equipment"
                                  :active-round-id="activeRoundId">
                        </ms-asset>
                    </section>
                    <section class="section-container" v-if="localAssets && localAssets.storeFurnishings.length">
                        <h3 class="section-header">
                            ↓ Sell Store Furnishing ↓
                            <md-icon v-if="furnishingsExpiringNow && furnishingsExpiringNow.length" class="fa fa-exclamation-circle"></md-icon>
                        </h3>
                        <ms-asset v-model="localAssets.storeFurnishings"
                                  :sale-elections.sync="currentStoreFurnishingSaleElections"
                                  :selected-assets.sync="soldStoreFurnishingIds"
                                  assetName="StoreFurnishing"
                                  :active-round-id="activeRoundId">
                        </ms-asset>
                    </section>
                    <div class="btn-wrapper">
                        <md-button @click="goToSpendCashStep()" class="md-raised btn-secondary fixed-bottom">Next, Spend Cash</md-button>
                    </div>
                </div>
            </div>
            <div class="inputSheetStep" v-if="routeDetail === 'spend-cash'">
                <div class="countdown" v-if="shouldShowCountdown">
                    <Countdown :deadline="currentDeadline" :countdown-id="'spend-cash'" :hide-labels="true" :hide-big-units="true"></Countdown>
                    <div class="remaining-label">remaining</div>
                </div>
                <div class="tab-header">
                    <h1>Spend Cash</h1>
                    Purchase assets & pay loans below
                </div>
                <section v-if="!noLand">
                    <h3 class="section-header">↓ Buy Land ↓</h3>
                    <land :land-purchase-elections.sync="currentLandPurchaseElections"></land>
                </section>
                <section v-if="!noBuilding">
                    <h3 class="section-header">↓ Buy Building ↓</h3>
                    <building :building-purchase-elections.sync="currentBuildingPurchaseElections"></building>
                </section>
                <section v-if="!noEquipment">
                    <h3 class="section-header">↓ Buy Equipment ↓</h3>
                    <equipment :equipment-purchase-elections.sync="currentEquipmentPurchaseElections"></equipment>
                </section>
                <section v-if="!noStoreFurnishing">
                    <h3 class="section-header">↓ Buy Store Furnishing ↓</h3>
                    <store-furnishing :store-furnishing-purchase-elections.sync="currentStoreFurnishingPurchaseElections"></store-furnishing>
                </section>
                <section v-if="localAssets && localAssets.debts.length">
                    <h3 class="section-header">
                        ↓ Debt Repayment ↓
                        <md-icon v-if="debtsDueNow && debtsDueNow.length" class="fa fa-exclamation-circle"></md-icon>
                    </h3>
                    <ms-debt-liability v-model="localAssets.debts"
                                    :debts-repayed-elections.sync="currentDebtsRepayedElections"
                                    :active-round-id="activeRoundId">
                    </ms-debt-liability>
                </section>
                <div class="btn-wrapper">
                    <md-button @click="goToMarketStep()" class="md-raised btn-secondary">Next, Market</md-button>
                </div>
            </div>
            <div class="inputSheetStep" v-if="routeDetail === 'go-to-market'">
                <div class="countdown" v-if="shouldShowCountdown">
                    <Countdown :deadline="currentDeadline" :countdown-id="'go-to-market'" :hide-labels="true" :hide-big-units="true"></Countdown>
                    <div class="remaining-label">remaining</div>
                </div>
                <div class="tab-header">
                    <h1>↓ Go to Market ↓</h1>
                    Get product ready for the market
                </div>
                <div class="tax-and-returns">
                  <div v-if="isSalesReturnsEnabled()">
                    <span class="md-body-1"><b>Sales Returns and Allowances This Round:</b> {{ (salesReturnsPercentage * 100).toFixed(2) }}%</span>
                  </div>
                  <div v-if="isIncomeTaxEnabled()">
                    <span class="md-body-1"><b>Income Tax Rate This Round:</b> {{ taxRate.toFixed(2) }}%</span>
                  </div>
                </div>
                <section v-if="!noProducts">
                    <h3 class="section-header">Product Offerings</h3>
                    <product v-if="activeRoundId"
                             :product-elections.sync="currentProductElections" :is-advertising-enabled="isAdvertisingEnabled()"
                             :advertising-config="getAdvertisingConfig()"
                             :is-product-saved.sync="isProductSaved"
                             :production-cost.sync="productionCost"
                             :assets.sync="localAssets"
                    >
                    </product>
                    <div v-show="!isSubmissionAllowed" class="validationError formError">You must meet all asset requirements before submitting</div>
                </section>
                <md-dialog-confirm md-confirm-text="Yes, Submit"
                                md-cancel-text="Pay Debt"
                                :md-active.sync="showOutstandingDebtModal"
                                md-title="Warning"
                                md-content="You currently have a debt due at the end of this round. If left unsettled, the remaining balance owed will automatically be deducted from Cash.<br><b>Caution:</b> Your team is at risk of accumulating a Junk Loan if this causes your cash to go negative.<br> Are you sure you want to submit?"
                                @md-cancel="goToUnpaidDebts()"
                                @md-confirm="submitFromDebtWarningModal()"
                ></md-dialog-confirm>
                <md-dialog-confirm md-confirm-text="Yes, Submit"
                                md-cancel-text="Cancel"
                                :md-active.sync="showProductSaveModal"
                                md-title="Warning"
                                md-content="You currently have unsaved products with values. Click Cancel to edit or choose to submit anyway."
                                @md-cancel="showProductSaveModal = false"
                                @md-confirm="submitFromProductWarningModal()"
                ></md-dialog-confirm>
                <div v-if="!shouldRequirePositiveCashBalanceBeforeElecting" class="allow-negative-cash-message">
                    This round, teams are permitted to produce more units than cash in their accounts. Beware, if your team doesn't recuperate expenditures from market sales numbers, it could result in expensive junk loans to offset your cash liability.
                </div>
                <div>
                    <div>
                        <Spinna v-if="httpWait"></Spinna>
                        <md-button @click="submitInputSheet()" :disabled="!isSubmissionAllowed || httpWait" v-bind:class="[{'disabled': !isSubmissionAllowed || httpWait}, 'md-raised', 'btn-primary', 'submit-btn']">Finalize and Submit Round</md-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import Building from './Building.vue';
import Equipment from './Equipment.vue';
import StoreFurnishing from './StoreFurnishing.vue';
import Stock from './Stock.vue';
import Bond from './Bond.vue';
import Land from './Land.vue';
import Debt from './Debt.vue';
import Product from './Product.vue';
import Countdown from './Countdown'
import MultiSelectAsset from './MultiSelectAsset.vue';
import MultiSelectDebtLiability from './MultiSelectDebtLiability.vue';
import { MdChips, MdButton, MdDialogConfirm, MdTooltip } from 'vue-material/dist/components';
import CurrentAssets from "@/components/CurrentAssets";
import { DateTime } from "luxon";
import Spinna from './Spinna.vue';
export default {
    data() {
        const result = {
            attrs: {
                currentElections: null,
                pastAssets: null,
                isSubmissionAllowed: false
            },
            httpWait: false,
            localAssets: null,
            showOutstandingDebtModal: false,
            showProductSaveModal: false,
            roundIndex: this.initRoundIndex,
            soldLandIds: [],
            soldBuildingIds: [],
            soldEquipmentIds: [],
            currentBuildingPurchaseElections: [],
            currentBuildingSaleElections: [],
            currentEquipmentPurchaseElections: [],
            currentEquipmentSaleElections: [],
            currentStoreFurnishingPurchaseElections: [],
            currentStoreFurnishingSaleElections: [],
            currentLandPurchaseElections: [],
            currentLandSaleElections: [],
            currentStockSaleElections: [],
            currentBondSaleElections: [],
            currentDebtsBorrowedElections: [],
            currentDebtsRepayedElections: [],
            currentProductElections: [],
            showFormError: false,
            currentEquipmentCount: 0,
            dismissAlert: false,
            debtsDueNow: null,
            ignoreWarning: false,
            buildingsExpiringNow: null,
            equipmentsExpiringNow: null,
            furnishingsExpiringNow: null,
            isProductSaved: true,
            currentGamePhase: null,
            errorMessage: "Cannot submit. Check form and try again",
            teamHasSubmittedElections: false,
            hasSuccessfullySubmitted: false,
            productionCost: 0,
            shouldShowInitialStockSalesMessaging: false,
            teamStartingStockPrice: null,
            initialOfferingNumShares: 0,
            totalSellableSharesPerTeam: 0,
            additionalStockSaleBenefit: 0,
            priorLoanAmount: 0,
            priorBondsSoldPrincipal: 0
        };
        return result
    },
    name: 'GameRound',
    components: {
        CurrentAssets,
        Countdown,
        'building': Building,
        'equipment': Equipment,
        'store-furnishing': StoreFurnishing,
        'stock': Stock,
        'land': Land,
        'debt': Debt,
        'product': Product,
        'ms-asset': MultiSelectAsset,
        'ms-debt-liability': MultiSelectDebtLiability,
        'bond': Bond,
        Spinna
    },
    computed: {
        ...mapGetters([
            'getCurrentRoundConfig',
            'getCurrentRoundElections',
            'getCurrentAssets',
            'getTeam',
            'getCurrentCourse',
            'getCurrentGameState',
            'getNextDeadline',
            'getFormattedDate'
        ]),
        isSubmissionAllowed: {
            get() {
                return this.attrs.isSubmissionAllowed;
            },
            set(value) {
                this.attrs.isSubmissionAllowed = value;
            }
        },
        noBuilding() {
            const roundConfig = this.getCurrentRoundConfig;
            if(!roundConfig) {
                return true;
            }
            return roundConfig.buildings.length === 0
        },
        noLand() {
            const roundConfig = this.getCurrentRoundConfig;
            if(!roundConfig) {
                return true;
            }
            return roundConfig.lands.length === 0
        },
        noDebt() {
            const roundConfig = this.getCurrentRoundConfig;
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            const borrowingCap = gameConfig.roundFeatures.borrowingCaps[roundIndex];
            if(!roundConfig) {
                return true;
            }
            const isDebtsEnabled = ((roundConfig.debts.length > 0) && (borrowingCap.debtRoundBorrowCap === -1 ||  borrowingCap.debtRoundBorrowCap > 0) && (borrowingCap.totalPrincipalCap === -1 ||  borrowingCap.totalPrincipalCap > 0))
            return !isDebtsEnabled;
        },
        noEquipment() {
            const roundConfig = this.getCurrentRoundConfig;
            if(!roundConfig) {
                return true;
            }
            return roundConfig.equipments.length === 0
        },
        noStoreFurnishing() {
            const roundConfig = this.getCurrentRoundConfig;
            if(!roundConfig) {
                return true;
            }
            return roundConfig.storeFurnishings.length === 0
        },
        noProducts() {
            const roundConfig = this.getCurrentRoundConfig;
            if(!roundConfig) {
                return true;
            }
            return roundConfig.products.length === 0
        },
        salesReturnsPercentage() {
            const team = this.getTeam;
            const salesReturnsForTeam = team.semester.teamSalesReturns[this.roundIndex].find((roundSalesReturn) => roundSalesReturn.teamId === team.id);
            return salesReturnsForTeam.assignedSalesReturnsPercentage;
        },
        taxRate() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            return gameConfig.roundFeatures.incomeTax[roundIndex];
        },
        shouldRequirePositiveCashBalanceBeforeElecting() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            return gameConfig.roundFeatures.requirePositiveCashBalanceBeforeElecting[roundIndex];
        },
        currentElections: {
            get() {
                return this.attrs.currentElections;
            },
            set(value) {
                this.attrs.currentElections = value;
            },
        },
        pastAssets: {
            get() {
                return this.attrs.pastAssets;
            },
            set(value) {
                this.attrs.pastAssets = value;
            }
        },
        loanCap: {
            get() {
                const gameConfig = this.getTeam.semester.gameConfig;
                const courseRounds = this.getCurrentCourse.rounds;
                const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
                return gameConfig.roundFeatures.borrowingCaps[roundIndex].debtRoundBorrowCap;
            }
        },
        bondCap: {
            get() {
                const gameConfig = this.getTeam.semester.gameConfig;
                const courseRounds = this.getCurrentCourse.rounds;
                const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
                return gameConfig.roundFeatures.borrowingCaps[roundIndex].bondRoundBorrowCap;
            }
        },
        totalBorrowCap: {
            get() {
                const gameConfig = this.getTeam.semester.gameConfig;
                const courseRounds = this.getCurrentCourse.rounds;
                const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
                return gameConfig.roundFeatures.borrowingCaps[roundIndex].totalPrincipalCap;
            }
        },
        currentDeadline() {
            return this.getNextDeadline;
        },
        shouldShowCountdown() {
            const nowSeconds = DateTime.local().toSeconds();
            const nextDeadlineSeconds = this.getNextDeadline;
            if(isNaN(nextDeadlineSeconds)) {
                return false;
            }
            // show countdown if time is less than 1 hour
            return nextDeadlineSeconds - nowSeconds <= 3599;
        },
        noStock() {
            // const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            const isStockTradingEnabled = courseRounds[roundIndex].isStockTradingEnabled;
            return !isStockTradingEnabled;
        },
        noBonds() {
            const roundConfig = this.getCurrentRoundConfig;
            if(!roundConfig) {
              return true;
            }
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            const borrowingCap = gameConfig.roundFeatures.borrowingCaps[roundIndex];
            return roundConfig.bonds.length === 0 || borrowingCap.bondRoundBorrowCap === 0 || borrowingCap.totalPrincipalCap === 0;
        },
        isEquipmentEnabledForGame() {
            const courseRounds = this.getCurrentCourse.rounds;
            for(let round of courseRounds) {
                if(round.equipments.length > 0) {
                    return true;
                }
            }
            return false;
        },
        isStoreFurnishingEnabledForGame() {
            const courseRounds = this.getCurrentCourse.rounds;
            for(let round of courseRounds) {
                if(round.storeFurnishings.length > 0) {
                    return true;
                }
            }
            return false;
        }
    },
    created() {
        this.initLocalAssets();
    },
    watch: {
        routeDetail(val) {
            if(val === 'get-cash' || val === 'spend-cash' || val === 'go-to-market') {
                this.hasSuccessfullySubmitted = false;
                this.$nextTick(() => {
                    const scrollParent = this.$el.closest('.game-accordion-view');
                    if (scrollParent) {
                        scrollParent.scrollTop = 0;
                    } else {
                        window.scrollTo(0, 0);
                    }
                });
            }
        },
        async activeRoundId(val) {
            if(val) {
                this.debtsDueNow = this.localAssets.debts.filter((debt) => {
                    return debt.repaymentRoundId === val;
                });
                this.buildingsExpiringNow = this.localAssets.buildings.filter((building) => {
                    return building.expiringRoundId === val;
                });
                this.equipmentsExpiringNow = this.localAssets.equipments.filter((equipment) => {
                    return equipment.expiringRoundId === val;
                });
                this.furnishingsExpiringNow = this.localAssets.storeFurnishings.filter((furnishing) => {
                    return furnishing.expiringRoundId === val;
                });

                this.getStockConfig();
            }
        },
        getCurrentAssets(val) {
            if(val) {
                this.initLocalAssets();
            }
        }
    },
    methods: {
        ...mapActions([
            'submitRoundElections',
            'fetchBeginningOfRoundStockPrice'
        ]),
        initLocalAssets() {
            // Clone the object (make it not a reference to VueX store)
            this.localAssets = JSON.parse(JSON.stringify(this.getCurrentAssets));
            const activeBuildings = this.localAssets.buildings.filter(building => {
                return building.isSold === false;
            });
            const activeLands = this.localAssets.lands.filter(land => {
                return land.isSold === false;
            });
            const activeEquipments = this.localAssets.equipments.filter(equipment => {
                return equipment.isSold === false;
            });
            const activeStoreFurnishings = this.localAssets.storeFurnishings.filter(furnishing => {
                return furnishing.isSold === false;
            });
            const activeDebts = this.localAssets.debts.filter(debt => {
                return debt.outstandingPrincipal !== 0;
            });
            this.localAssets.buildings = activeBuildings;
            this.localAssets.lands = activeLands;
            this.localAssets.equipments = activeEquipments;
            this.localAssets.storeFurnishings = activeStoreFurnishings;
            this.localAssets.debts = activeDebts;
            this.pastAssets = JSON.parse(JSON.stringify(this.getCurrentAssets));
            this.priorLoanAmount = this.pastAssets.debts.reduce((result, debtElection) => {
                return debtElection.originalPrincipal + result;
            }, 0);
            this.priorBondsSoldPrincipal = this.pastAssets.bonds.reduce((result, bondElection) => {
                result += bondElection.principal;
                return result;
            }, 0);
            this.initCurrentElections(this.getCurrentRoundElections);
            this.debtsDueNow = this.localAssets.debts.filter((debt) => {
                return debt.repaymentRoundId === this.activeRoundId;
            });
            this.buildingsExpiringNow = this.localAssets.buildings.filter((building) => {
                return building.expiringRoundId === this.activeRoundId;
            });
            this.equipmentsExpiringNow = this.localAssets.equipments.filter((equipment) => {
                return equipment.expiringRoundId === this.activeRoundId;
            });
            this.furnishingsExpiringNow = this.localAssets.storeFurnishings.filter((furnishing) => {
                return furnishing.expiringRoundId === this.activeRoundId;
            });
            const gameState = this.getCurrentGameState;
            this.currentGamePhase = gameState.phase;
            this.getStockConfig();
        },
        initCurrentElections(election) {
            this.currentElections = election;
            if(this.currentElections) {
                this.teamHasSubmittedElections = true;
                this.currentBuildingPurchaseElections = this.currentElections.buildingPurchases;
                this.currentBuildingSaleElections = this.currentElections.buildingSales;
                this.currentEquipmentPurchaseElections = this.currentElections.equipmentPurchases;
                this.currentEquipmentSaleElections = this.currentElections.equipmentSales;
                this.currentStoreFurnishingPurchaseElections = this.currentElections.storeFurnishingPurchases;
                this.currentStoreFurnishingSaleElections = this.currentElections.storeFurnishingSales;
                this.currentLandPurchaseElections = this.currentElections.landPurchases;
                this.currentLandSaleElections = this.currentElections.landSales;
                this.currentDebtsBorrowedElections = this.currentElections.debtsBorrowed;
                this.currentDebtsRepayedElections = this.currentElections.debtsRepayed;
                this.currentProductElections = this.currentElections.productElections;
                this.currentStockSaleElections = this.currentElections.stockSales;
                this.currentBondSaleElections = this.currentElections.bondSales;
            }
        },
        getStockConfig() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            this.teamStartingStockPrice = gameConfig.stockConfig.teamStartingStockPrice;
            this.initialOfferingNumShares = gameConfig.stockConfig.initialOfferingNumShares;
            this.shouldShowInitialStockSalesMessaging = roundIndex === 0 && (this.teamStartingStockPrice * this.initialOfferingNumShares) > 0;
            if(roundIndex === 0) {
                this.additionalStockSaleBenefit = this.teamStartingStockPrice * this.initialOfferingNumShares;
            }
        },
        async submitInputSheet() {
            const roundConfig = this.getCurrentRoundConfig;
            const input = {
                teamId: this.$route.params.teamId,
                roundConfigId: roundConfig.id,
                buildingPurchases: this.currentBuildingPurchaseElections,
                buildingSales: this.currentBuildingSaleElections,
                equipmentPurchases: this.currentEquipmentPurchaseElections,
                equipmentSales: this.currentEquipmentSaleElections,
                storeFurnishingPurchases: this.currentStoreFurnishingPurchaseElections,
                storeFurnishingSales: this.currentStoreFurnishingSaleElections,
                landPurchases: this.currentLandPurchaseElections,
                landSales: this.currentLandSaleElections,
                debtsBorrowed: this.currentDebtsBorrowedElections.map((debtBorrowed) => {
                    debtBorrowed.borrowingAmount = parseFloat(debtBorrowed.borrowingAmount);
                    return debtBorrowed;
                }),
                debtsRepayed: this.currentDebtsRepayedElections.map((debtRepayed) => {
                    debtRepayed.repaymentAmount = parseFloat(debtRepayed.repaymentAmount);
                    return debtRepayed;
                }),
                productElections: this.currentProductElections.map((productElection) => {
                    productElection.quantityOffered = parseInt(productElection.quantityOffered);
                    productElection.offerPrice = parseFloat(productElection.offerPrice);
                    productElection.numUnitsToAdvertise = parseInt(productElection.numUnitsToAdvertise);
                    return productElection;
                }),
                stockSales: this.currentStockSaleElections,
                bondSales: this.currentBondSaleElections
            };
            if((this.debtsDueNow && !this.ignoreWarning) || (!this.isProductSaved && !this.ignoreWarning)) {
                if(!this.isProductSaved) {
                    this.showProductSaveModal = true;
                }
                if(!input.debtsRepayed) {
                    this.showOutstandingDebtModal = true;
                } else {
                    for(let debt of this.debtsDueNow) {
                        if(!input.debtsRepayed.some((_debt) => {
                            return _debt.debtLiabilityId === debt.id && _debt.repaymentAmount === debt.outstandingPrincipal;
                        })) {
                            this.showOutstandingDebtModal = true;
                            break;
                        }
                    }
                }
            }
            if(!this.showOutstandingDebtModal && !this.showProductSaveModal) {
                this.httpWait = true;
                try{
                    await this.submitRoundElections(input);
                    this.hasSuccessfullySubmitted = true;
                    this.dismissAlert = false;
                    this.showFormError = false;
                    this.ignoreWarning = false;
                } catch (err) {
                    if(err == 'Error: GraphQL error: The market is closed for elections') {
                        this.errorMessage = "The market is closed for elections";
                    }
                    this.dismissAlert = false;
                    this.showFormError = true;
                }
            }
            this.httpWait = false;
        },
        submitFromDebtWarningModal() {
            this.showOutstandingDebtModal = false;
            if(this.showProductSaveModal) {
                return;
            } else {
                this.ignoreWarning = true;
                this.submitInputSheet();
            }
        },
        submitFromProductWarningModal() {
            this.showProductSaveModal = false;
            if(this.showOutstandingDebtModal) {
                return;
            } else {
                this.ignoreWarning = true;
                this.submitInputSheet();
            }
        },
        async goToUnpaidDebts() {
            await this.$router.push({
                name: 'Game',
                params: {
                    teamId: this.$route.params.teamId,
                    routeDetail: "spend-cash"
                }
            });
        },
        clearAlert() {
            this.dismissAlert = true;
        },
        isSalesReturnsEnabled() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            return !!gameConfig.roundFeatures.salesReturns[roundIndex];
        },
        isIncomeTaxEnabled() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            const taxConfig = gameConfig.roundFeatures.incomeTax[roundIndex];
            return taxConfig > 0;
        },
        isAdvertisingEnabled() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            const adConfig = gameConfig.roundFeatures.advertising[roundIndex];
            return adConfig.cost > 0 || adConfig.benefit > 0;
        },
        getAdvertisingConfig() {
            const gameConfig = this.getTeam.semester.gameConfig;
            const courseRounds = this.getCurrentCourse.rounds;
            const roundIndex = courseRounds.map((_round) => _round.id).indexOf(this.activeRoundId);
            return gameConfig.roundFeatures.advertising[roundIndex];
        },
        goToSpendCashStep() {
            this.$router.push({
                name: 'Game',
                params: {routeDetail: 'spend-cash', teamId: this.$route.params.teamId}
            });
        },
        goToMarketStep() {
            this.$router.push({
                name: 'Game',
                params: {routeDetail: 'go-to-market', teamId: this.$route.params.teamId}
            });
        }
    },
    props: {
        initRoundIndex: {
            type: Number,
        },
        routeDetail: {
            type: String
        },
        roundNavHeight: {
            type: Number
        },
        activeRoundId: {
            type: String
        }
    }
}
Vue.use(MdChips);
Vue.use(MdButton);
Vue.use(MdDialogConfirm);
Vue.use(MdTooltip);
</script>
<style scoped>

    .inputSheetStep {
        color: var(--text-primary);
    }
    .welcome {
      margin-top: 10px;
    }
    .md-layout-item {
        padding: .5em;
    }
    .formError {
        color: var(--btn-warning-color);
    }
    .formSuccess {
        color: green;
    }
    .preloaded-stock {
        margin-bottom: 15px;
        text-align: center;
    }
    .tax-and-returns {
        text-align: center;
        margin-bottom: 10px;
    }
    .gameBoard {
        margin: 1em;
        color: var(--text-primary);
        overflow-y: auto;
    }
    .section-header {
        padding: 1em;
        border-bottom: 3px solid black;
        margin-top: 1em;
        margin-bottom: 0;
    }
    .tab-header {
        padding: 15px;
    }
    .alert {
        margin-left: 1em;
        margin-right: 1em;
    }
    .alert-error {
        position: fixed;
        padding-top: 10px;
        padding-bottom: 10px;
        background-color: #ff1744;
        color: white;
        padding-left: 20px;
        padding-right: 20px;
        margin-left: auto;
        margin-right: auto;
        max-width: 700px;
    }
    .fa.fa-times-circle.light {
        color: white;
        cursor: pointer;
    }
    .game-round-container {
        padding-bottom: 64px;
    }

    .disabled {
        opacity: 0.7;
    }

    .validationError {
        background-color: var(--main-bg-color);
        margin-left: auto;
        margin-right: auto;
        min-width: 98%;
        max-width: 98%;
        padding: 0;
    }

    .center-text {
        text-align: center;
        font-weight: bold;
    }

    .countdown {
        position: absolute;
        left: 50%;
        transform: translate(-50%);
        top: -13px;
    }

    .remaining-label {
        margin-top: -8px;
    }

    .allow-negative-cash-message {
        padding-top: 20px;
        padding-bottom: 10px;
    }

    .submit-btn {
        background-color: rgb(19, 174, 71) !important;
    }

    .elections-alert {
        position: absolute;
        width: 33%;
        margin-left: 1em;
        max-width: 800px;
    }

    .elections-alert-mobile {
        display: none;
    }

    .md-icon.error {
        color: var(--warning-text-color);
    }

    .md-icon.success {
        color: var(--success-bg-color);
    }

    .md-icon.salmon {
        color: var(--salmon-dark);
    }

    @media screen and (max-width: 1015px) {
        .elections-alert {
            display: none;
        }
        .elections-alert-mobile {
            position: absolute;
            display: block;
            width: 65px;
        }
    }
</style>
